import { useContext, useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { capitalize } from '@/utils/strings'
import { formatDate } from '@/utils/formatters'
import { Badge } from '@/components/ui'
import { SingleLineAddressOnListing } from '@/components/Address'
import TabManager from '@/components/ui/TabManager'

import { setCurrentBien, fetchBien, resetBienInEdition } from './biensSlice'
import BienContext from './BienContext'
import InfoGenerales from './tabs/InfoGenerales'
import GestionVente from './tabs/GestionVente'
import Entretien from './tabs/Entretien'

// Composant pour le titre
const BienTitle = () => {
  const { bien } = useContext(BienContext)
  const [ locataireNames, setLocataireNames ] = useState(null)

  useEffect(() => {
    const location = bien.current_location
    if(location && location.status_for_locataire === 'en_cours') {
      setLocataireNames(location.locataires.map( contact => contact.nom_complet ?? "").join(" - "))
    } else {
      setLocataireNames(null)
    }
  }, [bien])

  return (
    <div className={`flex flex-col items-start mb-2 ${bien.archived_at && 'tooltip'}`}>
      {bien.archived_at && (
        <div className='tooltip-content'>
          archivé le {formatDate(bien.archived_at)}
        </div>
      )}
      <div className="font-bold flex flex-col items-start gap-2">
        <h1 className="flex text-xl">
          <SingleLineAddressOnListing data={bien} withApptNumber={false} />
          <div className="space-2 ml-2">
            {bien.type_bien !== 'appartement' ? ` ${capitalize(bien.type_bien)} ` : ' '}
            {bien.type_bien === 'appartement' ? ` ${bien.numero_appartement} ` : ' '}
            {bien.surface_carrez && `- ${bien.surface_carrez} m²`} {bien.prix && `- ${bien.prix} €`}
          </div>
        </h1>
        { locataireNames && <h1><span className="uppercase text-xl">{locataireNames}</span></h1>}
      </div>
      <div className="badge-container ml-2 flex gap-2">
        {bien.archived_at && <Badge text='archivé' />}
        {!bien.codage_interne && <div className="flex tooltip tooltip-right">
          <Badge text="CODAGE NON RENSEIGNÉ" color="error" />
          <div className="tooltip-content">Vous pouvez le saisir dans <span className="font-italic">«Informations générales»</span></div>
        </div>}
      </div>
    </div>
  )
}

const BienShow = ({bien}) => {
  const dispatch = useDispatch()
  const currentBien = useSelector((state) => state.biens.current)
  const loading = useSelector((state) => state.biens.loadingStatus.fetchItem)
  const updateLoading = useSelector((state) => state.biens.loadingStatus.update)
  const [isPendingAction, setIsPendingAction] = useState(false)
  const [wasUpdated, setWasUpdated] = useState(false)
  const [updatedSections, setUpdatedSections] = useState({})
  const [activeTab, setActiveTab] = useState('info-generales')

  // Exécuté uniquement au premier montage du composant
  useEffect(() => {
    dispatch(setCurrentBien(bien));
    dispatch(fetchBien(bien.id))
  }, [bien.id]) 

  useEffect(() => {
    if(loading && !isPendingAction) {
      setIsPendingAction(true)
    }
    if(!(loading || updateLoading) && isPendingAction) {
      setIsPendingAction(false)
    }
  }, [loading, updateLoading])

  useEffect(() => {
    if(updateLoading && !isPendingAction) {
      setIsPendingAction(true)
    }
    if (!updateLoading && isPendingAction) {
      // Mise à jour de setWasUpdated pour déclencher l'effet dans DetailTemplate
      // et garder le comportement existant pour les autres composants
      setWasUpdated(true)
      
      // Réinitialiser l'effet après un court délai
      const timer = setTimeout(() => {
        setWasUpdated(false)
        setUpdatedSections({})
      }, 2000)

      return () => clearTimeout(timer)
    }
  }, [updateLoading])

  // Nouvelle fonction pour signaler la mise à jour d'une section spécifique
  const markSectionAsUpdated = (sectionId) => {
    setUpdatedSections(prev => ({
      ...prev,
      [sectionId]: true
    }))
  }

  const tabs = [
    { 
      id: 'info-generales', 
      label: 'INFO GENERALES', 
      content: <InfoGenerales /> 
    },
    { 
      id: 'gestion-vente', 
      label: 'GESTION / VENTE', 
      content: <GestionVente /> 
    },
    { 
      id: 'entretien', 
      label: 'ENTRETIEN', 
      content: <Entretien /> 
    }
  ]

  return (
    <BienContext.Provider value={{
      bien: currentBien || bien, 
      wasUpdated,
      updatedSections,
      markSectionAsUpdated,
      activeTab, setActiveTab, 
      loading, isPendingAction,
      setIsPendingAction,
      closeModalCallback: () => {
        dispatch(resetBienInEdition())
      }
    }}>
      <div className="show-container">
        <BienTitle />
        <TabManager.Manager 
          tabs={tabs} 
          initialTab={activeTab}
        >
          <TabManager.Menu />
          <TabManager.Content />
        </TabManager.Manager>
      </div>
    </BienContext.Provider> 
  )
}

export default BienShow
